import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Flag, Store, Trash2, Clock, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import ProgressBar from "../components/ProgressBar";

function timeLeft(endDate) {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return "Ended — waiting to finalize";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h ${mins}m left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
}

export default function RoundDetail() {
  const { id } = useParams();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const r = await api.getRound(id);
      setRound(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Round not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [id]); // eslint-disable-line

  // Auto-refresh every 10s while round is active
  useEffect(() => {
    if (!round || round.status !== "active") return;
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [round?.status]);

  if (loading) return <div className="p-12 text-center text-sm text-gray-500">Loading round…</div>;
  if (!round) return <div className="p-12 text-center text-sm text-gray-500">Round not found</div>;

  const onFinalize = async () => {
    if (!window.confirm(`Finalize "${round.name}" now? This picks the top ${round.winnersCount} winner(s) immediately.`)) return;
    setFinalizing(true);
    try {
      const r = await api.finalizeRound(id);
      toast.success(`Round finalized — ${r.winners.length} winner(s) flipped to pre-order`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not finalize");
    } finally {
      setFinalizing(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete "${round.name}" and ALL its campaigns, votes, and pre-orders? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deleteRound(id);
      toast.success("Round deleted");
      navigate("/app/rounds");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
      setDeleting(false);
    }
  };

  const winners = (round.campaigns || []).slice(0, round.winnersCount);
  const losers = (round.campaigns || []).slice(round.winnersCount);
  const totalVotes = (round.campaigns || []).reduce((s, c) => s + (c.currentVoteCount || 0), 0);

  return (
    <div className="space-y-6" data-testid="round-detail-page">
      <div>
        <Link to="/app/rounds" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={14} /> Back to rounds
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="round-title">{round.name}</h1>
              {round.status === "active" ? (
                <span className="lp-badge lp-badge-voting">Active</span>
              ) : (
                <span className="lp-badge lp-badge-closed">Completed</span>
              )}
            </div>
            {round.description && <p className="text-sm text-gray-600 mt-2 max-w-2xl">{round.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/storefront/rounds/${round.id}`}
              className="lp-btn-secondary"
              data-testid="round-view-storefront-btn"
            >
              <Store size={16} /> View on storefront
            </Link>
            {round.status === "active" && (
              <button
                onClick={onFinalize}
                disabled={finalizing}
                className="lp-btn-primary"
                data-testid="finalize-round-btn"
              >
                <Flag size={16} />
                {finalizing ? "Finalizing…" : "Finalize now"}
              </button>
            )}
            <button
              onClick={onDelete}
              disabled={deleting}
              className="lp-btn-secondary text-red-600 hover:bg-red-50 hover:border-red-200"
              data-testid="delete-round-btn"
              title="Delete round"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Competing" value={(round.campaigns || []).length} icon={Trophy} />
        <Stat label="Winners" value={`Top ${round.winnersCount}`} icon={Crown} />
        <Stat label="Total votes" value={totalVotes} icon={Trophy} />
        <Stat
          label={round.status === "active" ? "Time left" : "Ended"}
          value={round.status === "active" ? timeLeft(round.endDate) : new Date(round.endDate).toLocaleDateString()}
          icon={Clock}
        />
      </div>

      {/* Live leaderboard */}
      <div className="lp-card overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-medium tracking-tight">Live leaderboard</h2>
          <p className="text-xs text-gray-500 mt-1">
            {round.status === "active"
              ? `Top ${round.winnersCount} highlighted — they win pre-order at deadline.`
              : "Final results. Winners advanced to pre-order; the rest were closed."}
          </p>
        </div>
        <div className="p-4 space-y-3" data-testid="round-leaderboard">
          {[...winners, ...losers].map((c, i) => {
            const isWinner = i < round.winnersCount;
            const pct = totalVotes > 0 ? Math.round((c.currentVoteCount / totalVotes) * 100) : 0;
            return (
              <div
                key={c.id}
                data-testid={`leaderboard-row-${i}`}
                className={`flex items-center gap-4 p-4 rounded-xl border ${
                  isWinner ? "border-amber-200 bg-amber-50/50" : "border-gray-200 bg-white"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
                  i === 0 ? "bg-amber-500 text-white" :
                  isWinner ? "bg-amber-200 text-amber-900" :
                  "bg-gray-100 text-gray-700"
                }`}>
                  {i + 1}
                </div>
                {c.productImageUrl && (
                  <img src={c.productImageUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900 truncate">{c.productTitle}</div>
                    {isWinner && round.status === "active" && (
                      <span className="lp-badge lp-badge-preorder text-[10px]">In winning zone</span>
                    )}
                    {c.status === "pre_order" && <span className="lp-badge lp-badge-preorder text-[10px]">Pre-order</span>}
                    {c.status === "closed" && <span className="lp-badge lp-badge-closed text-[10px]">Closed</span>}
                  </div>
                  <div className="mt-1.5">
                    <ProgressBar value={c.currentVoteCount} max={Math.max(1, totalVotes)} testId={`row-progress-${i}`} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xl font-semibold tabular-nums" data-testid={`row-votes-${i}`}>{c.currentVoteCount}</div>
                  <div className="text-xs text-gray-500">{pct}% of votes</div>
                </div>
              </div>
            );
          })}
          {(round.campaigns || []).length === 0 && (
            <div className="p-8 text-center text-sm text-gray-500">No campaigns in this round.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="lp-card p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="lp-section-label">{label}</div>
        <div className="text-2xl font-semibold tracking-tight mt-2">{value}</div>
      </div>
      <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center">
        <Icon size={16} className="text-gray-700" />
      </div>
    </div>
  </div>
);
